import React, { useEffect, useState } from 'react';
import { SelectPicker, Table, DOMHelper, Stack, IconButton, Input, Whisper, Message, Popover, useToaster, Button, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import FetchingSelect from '@/components/FetchingSelect';
import TablePagination from '@/components/TablePagination';
import { getCitiesLookup } from '@/services/city.service';
import { getCountriesLookup } from '@/services/country.service';
import { getUsersLookup } from '@/services/user.service';
import { Icon } from '@rsuite/icons';
import { MdList } from 'react-icons/md';
import { ActivityReport, ActivityReportPaginationFilterRequest, ActivityReportTypeList } from '@/models/activity-report.model';
import { deleteActivityReport, getPagedActivityReports } from '@/services/activity-report.service';
import { ActivityTypeList } from '@/models/activity-info.model';
import { jsDateToZone } from '@/utils/date-time';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;
interface ActivityReportListProps {
  userId?: number;
}

const ActivityReportList : React.FC<ActivityReportListProps> = ({userId}) => {
  const [filterReq, setFilterReq] = useState<ActivityReportPaginationFilterRequest>(new ActivityReportPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());  
  const [items, setItems] = useState<ActivityReport[]>([]);
  const [loading, setLoading] = useState(true);
  
  const toaster = useToaster();
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: ActivityReportPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        userId: req.userId ?? userId
      };
      const result: PagedResponseModel<ActivityReport> = await getPagedActivityReports(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching activity reports:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };
  
  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };
  
  const handleDeleteReport = async (id: number) => {
    const handleYesClick = async () => {
      setLoading(true);
      try {
        const result : BaseResponseModel<null> = await deleteActivityReport(id);
        if (result.success) {
          toastSuccessMessage(`Report Successfully Removed.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in deleting report:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
        toaster.clear(); 
      }
    };

    const handleNoClick = () => {
      toaster.clear(); 
    };

    toaster.push(
      <Message type="error" closable>
        <p style={{marginBottom:10}}>Are you sure you want to delete <strong>Report</strong>?</p>
        <div>
          <Button appearance="primary" color='red' onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );

  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          {!userId && <FetchingSelect
            label="User"
            fetchData={(text, page)=> getUsersLookup(text, undefined, page)}
            value={filterReq.userId}
            onChange={dataKey => setFilterReq(pre => ({...pre, userId: dataKey}))}
          />}
          <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />
          <FetchingSelect
            label="Country"
            fetchData={(text, page) => getCountriesLookup(text, page)}
            value={filterReq.countryId}
            onChange={dataKey => setFilterReq(pre => ({...pre, countryId: dataKey}))}
          />
          <SelectPicker
            label="Type"
            data={ActivityReportTypeList}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={dataKey => setFilterReq(pre =>({ ...pre, type: dataKey ?? undefined}))}
          />            
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >       
        <Column flexGrow={1.5} align="left" fullText>
          <HeaderCell>Report Type</HeaderCell>
          <Cell>
            {(rowData : ActivityReport) => ActivityReportTypeList.findLast(t => t.value == rowData.type)?.label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={5} align="left">
          <HeaderCell>Description</HeaderCell>
          <Cell>
            {
              (rowData : ActivityReport) => 
              <Whisper placement="topStart" trigger="hover" speaker={<Popover style={{maxWidth:500}}>{rowData.description}</Popover>}>
                <span>{rowData.description}</span>
              </Whisper>
            }
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="center" fullText>
          <HeaderCell>Act Id</HeaderCell>
          <Cell dataKey="activity.id" />
        </Column>
        <Column flexGrow={1} align="left" fullText>
          <HeaderCell>Activity Type</HeaderCell>
          <Cell>
            {(rowData : ActivityReport) => ActivityTypeList.findLast(t => t.value == rowData.activity.activityType)?.label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" fullText>
          <HeaderCell>Activity User</HeaderCell>
          <Cell>
            {(rowData: ActivityReport) => `${rowData.activity.user.firstName} ${rowData.activity.user.lastName}`}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" fullText>
          <HeaderCell>Activity City</HeaderCell>
          <Cell dataKey="activity.city.name" />
        </Column>
        <Column flexGrow={1} align="left" fullText>
          <HeaderCell>Activity Country</HeaderCell>
          <Cell dataKey="activity.country.name" />
        </Column>
        <Column flexGrow={1} align="center" fullText>
          <HeaderCell>Activity Start</HeaderCell>
          <Cell>
            {(rowData: ActivityReport) => jsDateToZone( rowData.activity.startTime, rowData.activity.city?.ianaTimeZoneId)}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" fullText>
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }} >
            {(rowData: ActivityReport) => 
              <Stack spacing={5}>
              <IconButton onClick={() => nav(`/activities/reports/${rowData.id}`)} icon={<Icon as={MdList} />} circle size="sm" />  
              </Stack>
            }
          </Cell>
        </Column>
      </Table>
      <Divider/>
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default ActivityReportList;
